import { z } from 'zod'

/**
 * Director Blackout Exceptions queue — shape of `GET /api/v1/escalations`.
 *
 * The api returns enough structured data for the web to render display
 * strings client-side. It deliberately doesn't pre-format labels like
 * "Q2 close · 18-23 Jun (full overlap)" — the page composes those from
 * the structured fields below.
 */

export const escalationOverlapKindSchema = z.enum(['full', 'partial'])
export type EscalationOverlapKind = z.infer<typeof escalationOverlapKindSchema>

export const escalationBlackoutLevelSchema = z.enum(['director', 'exec_director'])
export type EscalationBlackoutLevel = z.infer<typeof escalationBlackoutLevelSchema>

// The escalations endpoint returns leaves in ESCALATED (RFC 007 manager
// escalations, incl. unpaid) and PENDING_DIRECTOR_DECISION (legacy overflow /
// director-ladder rows). The enum must cover both or the whole array fails to
// parse and the queue silently errors out.
export const escalationLeaveStatusSchema = z.enum([
  'pending',
  'escalated',
  'pending_director_decision',
])
export type EscalationLeaveStatus = z.infer<typeof escalationLeaveStatusSchema>

export const escalationEmployeeSchema = z.object({
  id: z.guid(),
  name: z.string(),
  preferredName: z.string().nullable().default(null),
  teamName: z.string(),
})
export type EscalationEmployee = z.infer<typeof escalationEmployeeSchema>

export const escalationLeaveSchema = z.object({
  id: z.guid(),
  type: z.string(),
  startDate: z.string().date(),
  endDate: z.string().date(),
  days: z.number().int().min(1),
  reason: z.string(),
  status: escalationLeaveStatusSchema,
  submittedAt: z.string(),
  isEmergency: z.boolean(),
})
export type EscalationLeave = z.infer<typeof escalationLeaveSchema>

export const escalationBlackoutSchema = z.object({
  id: z.guid(),
  name: z.string(),
  startDate: z.string().date(),
  endDate: z.string().date(),
  level: escalationBlackoutLevelSchema,
  overlapKind: escalationOverlapKindSchema,
  overlapDays: z.number().int().min(1),
})
export type EscalationBlackout = z.infer<typeof escalationBlackoutSchema>

export const escalationRowSchema = z.object({
  id: z.guid(),
  employee: escalationEmployeeSchema,
  leave: escalationLeaveSchema,
  // Null for RFC 007 unpaid escalations (no blackout window). Present for
  // blackout-overlap escalations. The card renders each kind appropriately.
  blackout: escalationBlackoutSchema.nullable(),
  ageDays: z.number().int().nonnegative(),
})
export type EscalationRow = z.infer<typeof escalationRowSchema>

export const activeBlackoutSchema = z.object({
  id: z.guid(),
  name: z.string(),
  startDate: z.string().date(),
  endDate: z.string().date(),
  level: escalationBlackoutLevelSchema,
})
export type ActiveBlackout = z.infer<typeof activeBlackoutSchema>

export const escalationsResponseSchema = z.object({
  items: z.array(escalationRowSchema),
  activeBlackouts: z.array(activeBlackoutSchema),
})
export type EscalationsResponse = z.infer<typeof escalationsResponseSchema>

